import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Box, Button, GlobalStyles, Typography } from '@mui/material';
import FarChatResponse from './FarChatResponse';
import { normalizeV2Turn } from './api';
import { fetchPendingChatFeedback, type ChatFeedbackPending } from '../../api/stageFunctions';
import FeedbackPrompt from './FeedbackPrompt';
import { usePublishChatFeedbackLock } from './ChatFeedbackOverlays';

const SYNC_KEY = 'chat-feedback-state-changed';

function notifyOtherTabs() {
  try { localStorage.setItem(SYNC_KEY, `${Date.now()}:${Math.random()}`); } catch {
    // Storage may be disabled; server enforcement still protects other tabs.
  }
}

const ACTIONS = 'a, button, input, textarea, select, [role="button"], [tabindex], [contenteditable="true"]';
const FEEDBACK_CONTROLS = '[data-required-feedback-controls]';
const ANSWER_CONTROLS = '[data-chat-answer-content]';
const BUBBLE_READING_CONTROLS = 'button[data-chat-bubble-toggle]';
const ALLOWED = `${FEEDBACK_CONTROLS}, ${ANSWER_CONTROLS}, ${BUBBLE_READING_CONTROLS}`;
const ACTIVATION_CONTROLS = 'a, button, input, textarea, select, [role="button"], [contenteditable="true"]';

/** Lock app actions while keeping answer sources and feedback interactive. */
function lockOtherControls(requestAttention: () => void, attentionActive: () => boolean) {
  const previous = new Map<HTMLElement, { inert: boolean; aria: string | null }>();
  const focusFeedback = () => {
    (document.querySelector<HTMLElement>(`${FEEDBACK_CONTROLS} [data-testid="farchat-feedback-prompt"] button:not(:disabled), ${FEEDBACK_CONTROLS} input:not(:disabled)`)
      || document.querySelector<HTMLElement>(`${FEEDBACK_CONTROLS} button:not(:disabled)`))?.focus({ preventScroll: true });
  };
  const refresh = () => {
    document.querySelectorAll<HTMLElement>(ACTIONS).forEach((element) => {
      if (element.closest(ALLOWED) || element.querySelector(ALLOWED)) {
        const original = previous.get(element);
        if (original) {
          if (!original.inert) element.removeAttribute('inert');
          if (original.aria === null) element.removeAttribute('aria-disabled');
          else element.setAttribute('aria-disabled', original.aria);
          element.classList.remove('chat-feedback-disabled');
          previous.delete(element);
        }
        return;
      }
      if (!previous.has(element)) {
        previous.set(element, { inert: element.hasAttribute('inert'), aria: element.getAttribute('aria-disabled') });
        element.setAttribute('inert', '');
        element.setAttribute('aria-disabled', 'true');
        element.classList.add('chat-feedback-disabled');
      }
    });
  };
  const blockAction = (event: Event) => {
    if ((event.target as Element | null)?.closest(ALLOWED)) return;
    const control = (event.target as Element | null)?.closest(ACTIVATION_CONTROLS);
    const pointer = event as PointerEvent;
    // Inert/native-disabled controls may retarget a real pointer event to an
    // ancestor. Their visible bounds still identify the attempted action.
    const blockedPointer = event.type === 'pointerdown' && Array.from(previous.keys()).some(element => {
      if (!element.matches(ACTIVATION_CONTROLS)) return false;
      const bounds = element.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0 && pointer.clientX >= bounds.left && pointer.clientX <= bounds.right && pointer.clientY >= bounds.top && pointer.clientY <= bounds.bottom;
    });
    if (event.type === 'pointerdown' && !control && !blockedPointer) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.type === 'pointerdown' || event.type === 'click' && control && !(event as MouseEvent).detail) requestAttention();
  };
  const onFocus = (event: FocusEvent) => {
    if ((event.target as Element | null)?.closest(ALLOWED)) return;
    event.stopImmediatePropagation();
    if (attentionActive()) focusFeedback();
  };
  const onKey = (event: KeyboardEvent) => {
    const allowed = (event.target as Element | null)?.closest(ALLOWED);
    const control = (event.target as Element | null)?.closest(ACTIVATION_CONTROLS);
    if (!allowed && ['Enter', ' '].includes(event.key)
      && control && !(event.target as Element | null)?.matches('input, textarea, [contenteditable="true"]')) requestAttention();
    if (allowed && event.key === 'Tab') {
      const fields = Array.from(document.querySelectorAll<HTMLElement>(ACTIONS)).filter((element) => (
        element.closest(ALLOWED) && !element.matches(':disabled') && element.tabIndex >= 0
        && element.getAttribute('aria-hidden') !== 'true'
      ));
      const first = fields[0];
      const last = fields[fields.length - 1];
      if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
    }
    const readingKey = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)
      || event.key === ' ' && !control
      || (event.ctrlKey || event.metaKey) && ['a', 'c', 'f'].includes(event.key.toLowerCase());
    if (event.key === 'Escape' || (!allowed && !readingKey)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.key === 'Tab') focusFeedback();
    }
  };
  refresh();
  const observer = new MutationObserver(refresh);
  observer.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('click', blockAction, true);
  window.addEventListener('pointerdown', blockAction, true);
  window.addEventListener('submit', blockAction, true);
  window.addEventListener('drop', blockAction, true);
  window.addEventListener('dragstart', blockAction, true);
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('focusin', onFocus, true);
  return () => {
    observer.disconnect();
    window.removeEventListener('click', blockAction, true);
    window.removeEventListener('pointerdown', blockAction, true);
    window.removeEventListener('submit', blockAction, true);
    window.removeEventListener('drop', blockAction, true);
    window.removeEventListener('dragstart', blockAction, true);
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('focusin', onFocus, true);
    previous.forEach((value, element) => {
      if (!value.inert) element.removeAttribute('inert');
      if (value.aria === null) element.removeAttribute('aria-disabled');
      else element.setAttribute('aria-disabled', value.aria);
      element.classList.remove('chat-feedback-disabled');
    });
  };
}

/** App-wide coordinator; the required rating itself stays underneath its answer. */
const MandatoryChatFeedbackGate: React.FC<{ onRevealRecovery?: () => void }> = ({ onRevealRecovery }) => {
  const [pending, setPending] = useState<ChatFeedbackPending | null>(null);
  const [checking, setChecking] = useState(true);
  const [recoveryError, setRecoveryError] = useState(false);
  const [target, setTarget] = useState<{ element: HTMLElement; answerPresent: boolean } | null>(null);
  const [locatedPromptId, setLocatedPromptId] = useState<string | null>(null);
  const [attention, setAttention] = useState<{ promptId: string; revision: number } | null>(null);
  const pendingRef = useRef(pending); pendingRef.current = pending;
  const livePromptIds = useRef(new Set<string>());
  const attentionActive = !!pending && attention?.promptId === pending.prompt_id;
  const attentionRef = useRef(attentionActive); attentionRef.current = attentionActive;
  const requestAttention = useCallback(() => {
    const prompt = pendingRef.current;
    if (prompt) setAttention(previous => ({ promptId: prompt.prompt_id, revision: (previous?.revision ?? 0) + 1 }));
  }, []);
  const generation = useRef(0);
  const submittedIds = useRef(new Set<string>());
  const feedbackFormRef = useRef<HTMLDivElement | null>(null);
  const revealFeedback = useCallback(() => {
    const form = feedbackFormRef.current;
    form?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    form?.querySelector<HTMLElement>('[data-testid="farchat-feedback-prompt"] button:not(:disabled)')?.focus({ preventScroll: true });
  }, []);

  const recover = useCallback(async (blockWhileChecking: boolean, initialRecovery = false) => {
    const current = ++generation.current;
    if (blockWhileChecking) setChecking(true);
    try {
      const next = await fetchPendingChatFeedback();
      if (current !== generation.current) return;
      const recovered = next && !submittedIds.current.has(next.prompt_id) ? next : null;
      if (initialRecovery && recovered && !livePromptIds.current.has(recovered.prompt_id)) {
        setAttention(previous => ({ promptId: recovered.prompt_id, revision: (previous?.revision ?? 0) + 1 }));
      }
      setPending(previous => JSON.stringify(previous) === JSON.stringify(recovered) ? previous : recovered);
      setRecoveryError(false);
    } catch {
      if (current !== generation.current) return;
      // Status uncertainty cannot silently let a required prompt be bypassed.
      setRecoveryError(true);
    } finally {
      if (current === generation.current) setChecking(false);
    }
  }, []);

  useEffect(() => {
    const onRequired = (event: Event) => {
      const prompt = (event as CustomEvent<ChatFeedbackPending & { origin?: string }>).detail;
      if (prompt?.prompt_id && submittedIds.current.has(prompt.prompt_id)) return;
      if (prompt?.prompt_id && prompt.origin === 'live_answer') livePromptIds.current.add(prompt.prompt_id);
      if (prompt?.prompt_id) setPending(previous => {
        const next = previous?.prompt_id === prompt.prompt_id
          ? { ...previous, ...prompt, answer_turn: prompt.answer_turn ?? previous.answer_turn } : prompt;
        return JSON.stringify(previous) === JSON.stringify(next) ? previous : next;
      });
      notifyOtherTabs();
      void recover(true);
    };
    const onFocus = () => { void recover(false); };
    const onStorage = (event: StorageEvent) => {
      if (event.key === SYNC_KEY) void recover(true);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') onFocus(); };
    window.addEventListener('chat-feedback-required', onRequired);
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisible);
    void recover(true, true);
    return () => {
      generation.current += 1;
      window.removeEventListener('chat-feedback-required', onRequired);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [recover]);

  const locked = Boolean(pending) || checking || recoveryError;
  usePublishChatFeedbackLock(locked);
  useEffect(() => locked ? lockOtherControls(requestAttention, () => attentionRef.current) : undefined, [locked, requestAttention]);
  useEffect(() => {
    const turnId = pending?.turn_id ?? '';
    document.body.dataset.chatFeedbackPendingTurn = turnId;
    window.dispatchEvent(new CustomEvent('chat-feedback-pending-changed', { detail: turnId }));
    return () => {
      delete document.body.dataset.chatFeedbackPendingTurn;
      window.dispatchEvent(new CustomEvent('chat-feedback-pending-changed', { detail: '' }));
    };
  }, [pending?.turn_id]);

  useEffect(() => {
    if (!pending) { setTarget(null); return; }
    const locate = () => {
      // All answer renderers expose a slot, including historical answers.
      const visible = (element: HTMLElement) => {
        for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) {
          const style = window.getComputedStyle(ancestor);
          if (style.display === 'none' || style.visibility === 'hidden' || ancestor.getAttribute('aria-hidden') === 'true') return false;
        }
        return true;
      };
      const slot = Array.from(document.querySelectorAll<HTMLElement>('[data-chat-feedback-turn]'))
        .find((element) => element.dataset.chatFeedbackTurn === pending.turn_id && !element.closest('[data-chat-feedback-recovery]') && visible(element));
      const thread = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="home-chat-thread"], [data-testid="farchat-v2-thread"]')).find(visible);
      const host = Array.from(document.querySelectorAll<HTMLElement>('[data-chat-feedback-recovery-host]')).find(visible);
      const element = slot || thread || host;
      setLocatedPromptId(pending.prompt_id);
      setTarget((previous) => previous && previous.element === element && previous.answerPresent === Boolean(slot)
        ? previous : element ? { element, answerPresent: Boolean(slot) } : null);
    };
    locate();
    const observer = new MutationObserver(locate);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'aria-hidden'] });
    return () => observer.disconnect();
  }, [pending?.prompt_id, pending?.turn_id]);

  useEffect(() => {
    if (attentionActive && pending && locatedPromptId === pending.prompt_id && !target) onRevealRecovery?.();
  }, [attentionActive, pending?.prompt_id, locatedPromptId, target, onRevealRecovery]);
  useEffect(() => {
    if (attentionActive && pending && locatedPromptId === pending.prompt_id) revealFeedback();
  }, [attentionActive, attention?.revision, pending?.prompt_id, locatedPromptId, target, revealFeedback]);
  useEffect(() => {
    if (!attentionActive || !pending || locatedPromptId !== pending.prompt_id || typeof ResizeObserver === 'undefined') return;
    // Home summaries can arrive after the recovery form was positioned. Bring
    // the form back into view on that layout change, without interrupting typing.
    const observer = new ResizeObserver(() => {
      const form = feedbackFormRef.current;
      if (!form || form.contains(document.activeElement) && document.activeElement?.matches('input, textarea, [contenteditable="true"]')) return;
      const bounds = form.getBoundingClientRect();
      if (bounds.bottom < 0 || bounds.top > window.innerHeight) revealFeedback();
    });
    observer.observe(document.body);
    if (target?.element) observer.observe(target.element);
    return () => observer.disconnect();
  }, [attentionActive, pending?.prompt_id, locatedPromptId, target, revealFeedback]);

  const onSubmitted = () => {
    const saved = pending;
    if (pending) submittedIds.current.add(pending.prompt_id);
    generation.current += 1;
    setPending(null);
    setAttention(null);
    setChecking(false);
    setRecoveryError(false);
    notifyOtherTabs();
    if (saved && !target?.answerPresent) {
      window.dispatchEvent(new CustomEvent('chat-feedback-recovery-submitted', { detail: saved }));
    }
  };

  const form = pending && (
    <Box ref={feedbackFormRef} data-required-feedback-controls data-testid="mandatory-feedback-lock" sx={{ my: 1.5, maxWidth: '85%', p: 1.5,
      border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
      <FeedbackPrompt key={pending.prompt_id}
        turn={{ turn_id: pending.turn_id ?? '', user_msg: pending.question ?? '', summary: pending.answer ?? '', detail: '', citations: [], feedbackRequest: { promptId: pending.prompt_id } }}
        surface={pending.surface} contextId={pending.context_id ?? undefined} onSubmitted={onSubmitted} />
    </Box>
  );
  const recoveredTurn = useMemo(() => pending?.answer_turn ? normalizeV2Turn(pending.answer_turn) : {
    turn_id: pending?.turn_id ?? '', user_msg: pending?.question ?? '', summary: pending?.answer ?? '', detail: '', citations: [],
  }, [pending?.answer_turn, pending?.turn_id, pending?.question, pending?.answer]);
  const recoveredAnswer = pending && (
    <Box data-chat-feedback-recovery sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, py: 1 }}>
      <FarChatResponse turn={recoveredTurn}
        source={pending.surface} feedbackRecovery={!pending.answer_turn} workflowId={pending.context_id ?? null} threadId={null} />
      {form}
    </Box>
  );
  return <>
    <GlobalStyles styles={{ '.chat-feedback-disabled': { opacity: 0.45, filter: 'grayscale(1)', cursor: 'not-allowed', pointerEvents: 'none' } }} />
    {attentionActive && createPortal(<Alert data-required-feedback-controls severity="warning" variant="filled"
      sx={{ position: 'fixed', top: { xs: 64, sm: 80 }, left: '50%', transform: 'translateX(-50%)', width: 'min(92vw, 800px)', boxSizing: 'border-box', zIndex: theme => theme.zIndex.modal + 10, boxShadow: 4, fontWeight: 700 }}
      action={<Button color="inherit" onClick={revealFeedback}>Go to feedback</Button>}>
      To proceed, you must answer this feedback.
    </Alert>, document.body)}
    {pending && locatedPromptId === pending.prompt_id ? target
      ? createPortal(target.answerPresent ? form : recoveredAnswer, target.element)
      : <Box sx={{ mx: 'auto', maxWidth: 1000, p: 2 }}>{recoveredAnswer}</Box>
      : locked && <Box data-required-feedback-controls data-testid="mandatory-feedback-lock" role="status" sx={{ m: 2 }}>
        {recoveryError ? <>
          <Alert severity="error">Unable to check required feedback. Please retry to continue.</Alert>
          <Button variant="contained" onClick={() => { void recover(true); }}>Retry</Button>
        </> : <Typography>Checking required feedback…</Typography>}
      </Box>}
  </>;
};
export default MandatoryChatFeedbackGate;
